import { useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, useParams } from 'react-router-dom';

import { approvalsGetApprovalOptions } from '@column/api';
import { Button, Icon, Toggle } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { TransferReview } from '~/app/pages/Transfers/_components/TransferReview';
import { buildWireEvents } from '~/app/pages/Transfers/_utils/wire';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, RenderFields } from '~/components';
import { useAccountNumber, useBankAccount, useCounterparty, useEntity } from '~/hooks';
import { useReviewWireTransfer, useTransfer } from '~/hooks/useTransfers';
import { useNavigate } from '~/lib/navigation';
import { SimulateRepository, WireTransfer, convertTransferAmount } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ReviewTransferRequest, WireWireTransferStatus } from '~/typings/enum';

import { InfoForm, TransferPageHeader } from './_components/Layout';
import { Timeline } from './_components/Timeline';

interface Params {
  id: string;
}

const formatData = (transfer: WireTransfer): Record<string, unknown> => {
  const formatted: Record<string, unknown> = {
    fiToFiInformation: {
      line1: transfer.fiToFiInformationLine1 || null,
      line2: transfer.fiToFiInformationLine2 || null,
      line3: transfer.fiToFiInformationLine3 || null,
      line4: transfer.fiToFiInformationLine4 || null,
      line5: transfer.fiToFiInformationLine5 || null,
      line6: transfer.fiToFiInformationLine6 || null,
    },
    originatorToBeneficiaryInformation: {
      line1: transfer.originatorToBeneficiaryInformationLine1 || null,
      line2: transfer.originatorToBeneficiaryInformationLine2 || null,
      line3: transfer.originatorToBeneficiaryInformationLine3 || null,
      line4: transfer.originatorToBeneficiaryInformationLine4 || null,
    },
    senderAddress: transfer.senderAddress
      ? {
          line1: transfer.senderAddress.line1?.toString() || null,
          line2: transfer.senderAddress.line2?.toString() || null,
          city: transfer.senderAddress.city?.toString() || null,
          state: transfer.senderAddress.state?.toString() || null,
          postalCode: transfer.senderAddress.postalCode?.toString() || null,
          countryCode: transfer.senderAddress.countryCode?.toString() || null,
        }
      : null,
  };
  Object.entries(transfer).forEach(([key, value]) => {
    if (
      key.startsWith('fiToFiInformation') ||
      key.startsWith('originatorToBeneficiaryInformation') ||
      key.startsWith('senderAddress')
    ) {
      return;
    }
    formatted[key] = value;
  });
  return formatted;
};

export const PageTransfersWire: FC = () => {
  const { id } = useParams<keyof Params>() as Params;

  // Redirect wire drawdowns to the new route
  if (id.startsWith('wdrw_')) {
    return <Navigate to={`${ROUTE.TRANSFERS}/edit/wire/drawdown/${id}`} replace />;
  }

  const { isAuthorized, isSandbox } = useSessionStore();
  const navigate = useNavigate();
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();

  const {
    response: bankAccount,
    isLoading: isBankAccountLoading,
    createRequest: fetchBankAccount,
  } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: accountNumber,
    isLoading: isAccountNumberLoading,
    createRequest: fetchAccountNumber,
  } = useAccountNumber({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: counterparty,
    isLoading: isCounterpartyLoading,
    createRequest: fetchCounterparty,
  } = useCounterparty({
    onError: (error) => addDangerNotification({ content: error.message }),
  });

  const [isLoadingEntity, setIsLoadingEntity] = useState(false);
  const { response: entity, createRequest: fetchEntity } = useEntity({
    onSuccess: () => setIsLoadingEntity(false),
    onError: (error) => {
      setIsLoadingEntity(false);
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: originalTransfer,
    isLoading: isTransferLoading,
    createRequest: fetchTransfer,
  } = useTransfer<WireTransfer>('wire')({
    initialParams: { id },
    onSuccess: async (response) => {
      fetchAccountNumber({ id: response.accountNumberId });

      try {
        if (response.bankAccountId) {
          const currentBankAccount = await fetchBankAccount({ id: response.bankAccountId });

          if (!currentBankAccount) {
            return;
          }

          if (isAuthorized({ permission: 'entities', permissionLevel: 'read' })) {
            fetchEntity({ id: currentBankAccount.owners?.[0] });
            setIsLoadingEntity(true);
          }
        }
      } catch (error) {
        addDangerNotification({ content: (error as Error).message });
      }

      if (response?.counterpartyId) {
        fetchCounterparty({ id: response.counterpartyId });
      }
    },

    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  useEffect(() => {
    if (id) {
      fetchTransfer({ id });
    }
  }, [id]);

  const [isReviewLoading, setReviewLoading] = useState(false);
  const { response: reviewedTransfer, createRequest: createReviewReq } = useReviewWireTransfer({
    onError: () => setReviewLoading(false),
    onSuccess: () => setReviewLoading(false),
  });
  const reviewTransfer = useCallback(
    async (req: ReviewTransferRequest) => {
      setReviewLoading(true);
      try {
        await createReviewReq({ id, ...req });
      } catch (err) {
        addDangerNotification({
          content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
        });
      }
    },
    [id]
  );
  const transfer = reviewedTransfer || originalTransfer;

  const { data: approvalState } = useQuery({
    ...approvalsGetApprovalOptions({ path: { object_id: id } }),
    enabled: transfer?.status === WireWireTransferStatus.PRE_REVIEW,
  });
  const formattedTransfer = useMemo(() => (transfer ? formatData(transfer) : undefined), [transfer]);

  const formMethods = useForm<WireTransfer>();

  useEffect(() => {
    if (transfer) {
      formMethods.setValue('description', transfer.description || '-');
      formMethods.setValue('idempotencyKey', transfer.idempotencyKey || '-');
      formMethods.setValue('allowOverdraft', transfer.allowOverdraft || false);
    }
  }, [transfer]);

  const isDataLoading = useMemo(() => {
    return isAccountNumberLoading || isBankAccountLoading || isCounterpartyLoading || isTransferLoading;
  }, [isAccountNumberLoading, isBankAccountLoading, isCounterpartyLoading, isTransferLoading]);

  const handleSettle = useCallback(() => {
    if (!transfer) return;
    SimulateRepository.settleWire({ wireTransferId: transfer.id })
      .then(() => {
        fetchTransfer();
        addSuccessNotification({
          content: 'Wire transfer settled',
        });
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message,
        });
      });
  }, [isSandbox, transfer]);

  return (
    <>
      <PageActions>
        {isSandbox &&
          transfer?.status &&
          !['COMPLETED', 'REJECTED', 'SETTLED', 'CANCELED', 'RETURNED', 'PRE_REVIEW'].includes(transfer.status) && (
            <Button onClick={handleSettle} variant="primary" size="small" icon={<Icon.Sandbox />}>
              Settle
            </Button>
          )}
      </PageActions>

      <Loading isLoading={isDataLoading} fullHeight>
        {transfer && (
          <TransferPageHeader
            amount={transfer.amount!}
            createdAt={transfer.createdAt}
            isAdding={transfer ? convertTransferAmount('wire', transfer.isIncoming) > 0 : undefined}
            direction={{ type: transfer.isIncoming ? 'incoming' : 'outgoing' }}
            status={transfer.status!}
            transferId={transfer.id!}
            transferType="wire"
          />
        )}
        <TransferReview
          amount={transfer?.amount}
          bankAccountName={bankAccount?.displayName || bankAccount?.description || 'Unnamed Bank Account'}
          counterpartyName={counterparty?.name || counterparty?.description || 'Unnamed Counterparty'}
          createdByName={transfer?.createdBy?.name}
          description={transfer?.description}
          isDisabled={
            !isAuthorized({ permission: 'approveTransfers', permissionLevel: 'write', bankAccount }) ||
            !approvalState?.can_review
          }
          isLoading={isReviewLoading}
          isOpen={!!transfer && transfer.status === WireWireTransferStatus.PRE_REVIEW}
          onSubmit={reviewTransfer}
        />
        {bankAccount && accountNumber && counterparty && transfer && !isLoadingEntity && (
          <Timeline
            entries={buildWireEvents({
              transfer,
              account: {
                accountNumber,
                bankAccount,
                entityType: entity ? (entity.type.toLowerCase() as 'person' | 'business') : undefined,
              },
              counterparty,
            })}
          />
        )}
        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<WireTransfer>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} />;
                      },
                    },
                    {
                      id: 'idempotencyKey',
                      label: 'Idempotency Key',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || ''} isMonospaced />;
                      },
                    },
                    {
                      id: 'allowOverdraft',
                      label: 'Allow Overdraft',
                      children: ({ value, onChange }) => {
                        const isChecked = typeof value === 'boolean' ? value : false;
                        return <Toggle isChecked={isChecked} onCheckedChange={onChange} isDisabled />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Details
              label="Transfer Details"
              formattedData={formattedTransfer}
              rawData={transfer as Record<string, unknown> | undefined}
            />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};
